
import { Component, Emit, Prop, Vue, Watch } from "vue-property-decorator";
import vselect3 from "vselect3";
import {
	ClearanceFilterLevels,
	LockdownConfigurationByDesignation,
	UpdatedLockdownClearanceByName
} from '@/store/lockdown/types';
import { Mutation, namespace, State, Getter } from 'vuex-class';

const Lockdown = namespace("lockdown");

@Component({
	components: {
		"v-select-3": vselect3
	}
})
export default class ClearanceFilterLevelEdit extends Vue {
	@Prop({ type: Object }) private selectedConfiguration: LockdownConfigurationByDesignation;
	@Prop({ type: Array }) private selectedConfigurations: LockdownConfigurationByDesignation[];

	@Lockdown.State(state => state.lockdownConfigurations) private lockdownConfigurations: LockdownConfigurationByDesignation[];
	@Lockdown.State private selectedClearanceLevel: number;
	@Lockdown.Action private updateClearanceFilterLevel: (updatedLockdownDetailsByName: UpdatedLockdownClearanceByName[]) => Promise<boolean>;
	@Lockdown.Mutation private setAllConfigurationsDeselected:() => void;
	@Lockdown.Mutation private setUpdateReasonOnLockdownConfiguration:(reason: string) => void;
	@Lockdown.Mutation private setSelectedClearanceFilterLevel:(clearanceLevel: number) => void;

	@Mutation private setIsLoading: (loading: boolean) => void;
	@State(State => State.User.requireReasonUpdatingClearanceOnReaderGroups) requireReason: boolean;
	@Getter getFeature: (featureNames: string[]) => boolean;

	private selectedClearanceFilterLevel = null;
	private updateReason: string = "";

	@Watch("selectedClearanceFilterLevel")
	public onSelectedClearanceFilterLevelChanged(newValue: number) 
	{
		this.setSelectedClearanceFilterLevel(newValue);
	}

	public get isReasonFieldEnabled(): boolean {
		return this.getFeature(["Integration", "CCure", "UI", "Lockdown", "Reason"]);
	}

	private get isReasonProvided(): boolean {
		if (this.isReasonRequired && this.isReasonFieldEnabled) {
			if (!this.updateReason) {
				return false;
			}
			return this.updateReason.length > 0;
		}
		return true;
	}

	private get isReasonRequired(): boolean {
		return this.requireReason && this.isReasonFieldEnabled;
	}

	private created(): void {
		if (this.selectedConfiguration) {
			this.selectedClearanceFilterLevel = this.selectedConfiguration.clearanceFilterLevel;
		}

		if (this.selectedClearanceFilterLevel) {
			this.setSelectedClearanceFilterLevel(this.selectedClearanceFilterLevel);
		}
	}

	private get configTitle(): string {
		return this.selectedConfiguration ? 
			`${this.selectedConfiguration.serverTitle} ${this.selectedConfiguration.isDesignated ? "Designated" : "Undesignated"}` : 
			`${this.selectedConfigurations.length} configurations selected`;
	}

	private get clearanceFilterLevels(): number[] {
		return ClearanceFilterLevels;
	}

	private get saveShouldBeDisabled(): boolean {
		return (
			this.selectedClearanceLevel === null ||
			this.selectedConfiguration?.clearanceFilterLevel === this.selectedClearanceLevel ||
			!this.isReasonProvided
		);
	}

	private get notificationMessage(): string {
		return (
			`Successfully set to level ${this.selectedClearanceLevel}`
		);
	}

	private handleModalHidden(): void {
		this.closeModal();
	}

	/**
	 * Modal is used both in the case of updating a single reader group or a subset
	 * In the case of a single reader, update it individually
	 * In the case of multiple readers, update them all
	 */
	private async handleSaveClick(): Promise<void> {
		if (!this.selectedConfiguration && !this.selectedConfigurations) {
			return;
		}

		this.setIsLoading(true);

		if (this.isReasonRequired && !this.updateReason) {
			return;
		} else if(this.isReasonRequired || (this.isReasonFieldEnabled && this.updateReason)) {
			this.setUpdateReasonOnLockdownConfiguration(this.updateReason);
		}

		const updatedClearanceLevels = this.getUpdatedClearanceLevelsByName();

		try {
			const result = await this.updateClearanceFilterLevel(updatedClearanceLevels);

			if(result) {
				this.$notify({
					type: "success",
					title: "Clearance Filter Level Updated",
					text: "Success"
				});
			}
		} catch {
			this.$notify(
				{ type: "error", title: "Error", text: "Error updating lockdown configuration, please try again later." });
			return;
		} finally {
			this.setIsLoading(false);
			this.handleModalHidden();
			this.updateReason = "";
			this.setUpdateReasonOnLockdownConfiguration("");
		}

		this.setAllConfigurationsDeselected();
	}

	private getUpdatedClearanceLevelsByName(): UpdatedLockdownClearanceByName[] {
		let namesUpdated: UpdatedLockdownClearanceByName[] = [];

		if (this.selectedConfiguration) {
			namesUpdated.push({
				name: this.selectedConfiguration.name,
				level: this.selectedClearanceFilterLevel
			});
		} else if (this.selectedConfigurations) {			
			this.selectedConfigurations.forEach((config: LockdownConfigurationByDesignation) => {
				if (config.selected) {
					namesUpdated.push({
						name: config.name,
						level: this.selectedClearanceFilterLevel
					});
				}
			});
		}

		return namesUpdated;
	}

	private get availableClearanceLevels(): number[] {
		if (this.selectedConfiguration) {
			return this.selectedConfiguration ? this.selectedConfiguration.availableClearanceLevels : [];
		} else {
			if (this.selectedConfigurations && this.selectedConfigurations.length > 0) {
				return this.selectedConfigurations[0].availableClearanceLevels;
			}

			return []; 
		}
	}

	@Emit("modal-closed")
	private closeModal(): void {
		this.updateReason = "";
	}
}
